import {
  MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { useRecoilState } from "recoil";
import { useMutation, useQuery } from "@apollo/client";
import { useTheme } from "styled-components";
import videojs from "video.js";
import { FaVolumeMute, FaVolumeUp } from "react-icons/fa";
import {
  FiArrowRight,
  FiAward,
  FiCheckCircle,
  FiFileText,
  FiFlag,
  FiUsers,
} from "react-icons/fi";

import { Avatar } from "MVVM/Components/AvatarV2";
import { ClipBadges } from "MVVM/Components/ClipBadge";
import { ClipStatBar } from "MVVM/Components/ClipStatBar";
import { LoginUpsell } from "MVVM/Components/LoginUpsell";
import { PlayButton } from "MVVM/Components/PlayButton";
import { VideoPlayer } from "MVVM/VideoPlayer";
import { analytics } from "MVVM/Analytics";
import { MUTATE_REACTION } from "MVVM/GraphQL/addReaction";
import {
  GET_ACTIVITY_FEED_CLIP_EXTRAS,
  GET_ACTIVITY_FEED_COMPETITION_EXTRAS,
} from "MVVM/GraphQL/homepage";
import { usePortal } from "MVVM/Hooks/usePortal";
import { useUser } from "MVVM/Hooks/useUser";
import { ESupportedGames, TUserGroup } from "MVVM/@types";
import { ClipAutoplayMutedActivityFeed } from "MVVM/State/clip";
import { dateFormatWithDistanceSuffix } from "MVVM/Utilities/Clip";
import {
  COMPETITION_PLACEHOLDER_BANNER,
  getCompetitionPhase,
  getPrizeLabel,
  getRuleChips,
  rankAccent,
} from "../../../../Competitions/utils";
import {
  pad,
  useCountdownParts,
} from "../../../../Competitions/useCountdownParts";
import { TopComment } from "../../../ClipOfTheDay/TopComment";
import { getReasonLabel } from "../reasonConfig";
import { PlayedMatchFeedCard } from "./PlayedMatchFeedCard";
import {
  IActivityFeedBlogPostItem,
  IActivityFeedClipExtras,
  IActivityFeedClipItem,
  IActivityFeedCompetitionExtras,
  IActivityFeedCompetitionItem,
  IActivityFeedNode,
} from "../@types";
import {
  BlogAuthorGroup,
  BlogAuthorName,
  BlogAvatar,
  BlogBackgroundAvatar,
  BlogByline,
  BlogCard,
  BlogContent,
  BlogTag,
  BlogTitle,
  Card,
  CompetitionBanner,
  CompetitionByline,
  CompetitionCard,
  CompetitionChip,
  CompetitionChipsRow,
  CompetitionCountdownBox,
  CompetitionCountdownCell,
  CompetitionCountdownDigits,
  CompetitionCountdownLabel,
  CompetitionCountdownNum,
  CompetitionCountdownSep,
  CompetitionCountdownUnit,
  CompetitionCreatorLabel,
  CompetitionCreatorLink,
  CompetitionCreatorName,
  CompetitionEndedRow,
  CompetitionMedia,
  CompetitionMetaCount,
  CompetitionMetaDivider,
  CompetitionOfficialTag,
  CompetitionOverlay,
  CompetitionPrizeBlock,
  CompetitionPrizeLabel,
  CompetitionPrizeValue,
  CompetitionScrim,
  CompetitionStatusBadge,
  CompetitionTitle,
  ClipAvatarLink,
  ClipTitle,
  ClipUsernameLink,
  PromotedCta,
  PromotedCtaAction,
  PromotedCtaBody,
  PromotedCtaMeta,
  PromotedCtaPrize,
  PromotedCtaTitle,
  PromotedEyebrow,
  TagsRow,
  Header,
  HeaderLine,
  HeaderMeta,
  HeaderText,
  LiveDot,
  MuteToggle,
  PlayOverlay,
  PreloadVideo,
  StatOverlay,
  Thumbnail,
  ThumbnailWrapper,
  VideoOverlay,
  WinnerAvatarRing,
  WinnerFeatureAvatar,
  WinnerFeatureItem,
  WinnerFeatureName,
  WinnerFeatureScore,
  WinnerRankBadge,
  WinnersFeatureRow,
  WinnersHeading,
  WinnersSection,
} from "./styledComponents";
import { trackFeedClick } from "../../analytics";

const endsInLabel = (endsAt?: string | null): string | null => {
  if (!endsAt) return null;
  const ms = new Date(endsAt).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return null;
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return `${days} day${days === 1 ? "" : "s"} left`;
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 1) return `${hours} hour${hours === 1 ? "" : "s"} left`;
  return "Ends within the hour";
};

const GAME_LABELS: Record<number, string> = {
  [ESupportedGames.CS2]: "CS2",
  [ESupportedGames.CSGO]: "CS:GO",
  [ESupportedGames.DOTA2]: "Dota 2",
  [ESupportedGames.FORTNITE]: "Fortnite",
  [ESupportedGames.LOL]: "LoL",
  [ESupportedGames.OW2]: "Overwatch 2",
  [ESupportedGames.VALORANT]: "Valorant",
};

/* Clips run seconds, not minutes, so "0:08" reads better here than the
   zero-padded "00:08" secondsToMinutesAndSeconds returns. */
const formatClipDuration = (seconds: number) => {
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
};

const ClipFeedCard = ({
  item,
  reason,
  isActive,
  preload,
  registerRef,
}: {
  item: IActivityFeedClipItem;
  reason: string;
  isActive: boolean;
  preload: boolean;
  registerRef: (node: HTMLElement | null) => void;
}) => {
  const history = useHistory();
  const videoPlayerRef = useRef<videojs.Player | null>(null);
  const [muted, setMuted] = useRecoilState(ClipAutoplayMutedActivityFeed);

  // The mute preference is shared across every autoplaying clip on the page,
  // so a toggle anywhere has to reach this player too. onPlay alone only
  // covers playback that starts *after* the change. Gated on hasStarted so
  // it can't unmute ahead of the first play and trip autoplay policy.
  useEffect(() => {
    const player = videoPlayerRef.current;
    // isDisposed: VideoPlayer's cleanup nulls its own ref but not the one
    // handed in via refForVideoPlayer, so this can still be holding a torn
    // down player whose tech element is gone — muting that throws.
    if (!player || player.isDisposed?.() || !player.hasStarted?.()) return;
    player.muted(muted);
  }, [muted]);

  // The full-quality video url is only needed once this card is active or
  // about to be (adjacent card, about to be scrolled to) — fetched lazily
  // here instead of bloating the bulk feed query for every card.
  const { data: extrasData } = useQuery(GET_ACTIVITY_FEED_CLIP_EXTRAS, {
    variables: { id: item.id },
    skip: !isActive && !preload,
  });
  const extras: IActivityFeedClipExtras | undefined = extrasData?.clip;

  const [hasLiked, setHasLiked] = useState(false);
  const commentCount = item.comments?.pageInfo?.totalCount ?? 0;
  const likeCount =
    (item.reactions ?? []).reduce((sum, r) => sum + r.count, 0) +
    (hasLiked ? 1 : 0);

  const { allstarUser } = useUser();
  const [mutateReaction] = useMutation(MUTATE_REACTION, {
    ignoreResults: true,
  });
  const { portalOpen, setPortalOpen, Portal } = usePortal(
    <LoginUpsell ctaText="Like" />,
  );

  const openClip = () => {
    analytics.track("Activity Feed - Watch", { shareId: item.shareId, reason });
    history.push(`/clip/${item.shareId}`);
  };
  const openComments = () => {
    trackFeedClick("comment", { shareId: item.shareId });
    history.push(`/clip/${item.shareId}?tab=comments`);
  };

  const toggleMute = (event: ReactMouseEvent) => {
    event.stopPropagation();
    const next = !muted;
    videoPlayerRef.current?.muted(next);
    setMuted(next);
    analytics.track(next ? "Activity Feed - Mute" : "Activity Feed - Unmute", {
      shareId: item.shareId,
    });
  };

  const goToProfile = (event: ReactMouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    trackFeedClick("profile", { username: item.username });
    history.push(`/u/${item.username}`);
  };

  const submitLike = () => {
    trackFeedClick("react", { shareId: item.shareId });
    if (!allstarUser.loggedIn) return setPortalOpen(true);
    mutateReaction({
      variables: {
        input: { clipId: item.internalId || item.shareId, reaction: "FIRE" },
      },
    });
    setHasLiked(true);
  };

  const validTags = (item.tags ?? []).filter(
    (tag): tag is { key: string; value: string } =>
      Boolean(tag.key && tag.value),
  );

  const videoSrc = extras?.url || item.previewUrl;
  const canAutoplay = isActive && !!videoSrc;
  const reasonLabel = getReasonLabel(reason);

  const metaLine = [
    item.game ? GAME_LABELS[item.game] : null,
    item.duration ? formatClipDuration(item.duration) : null,
    dateFormatWithDistanceSuffix(item.createdAt),
  ]
    .filter(Boolean)
    .join(" \u2022 ");

  const promoted = item.promotedCompetition;
  const promotedPrize = promoted ? getPrizeLabel(promoted.prizes) : null;
  const hasPrize = !!promotedPrize && promotedPrize !== "Just for fun";
  const promotedEndsIn = endsInLabel(promoted?.endsAt);

  return (
    <Card
      $promoted={!!promoted}
      $banner={
        promoted
          ? promoted.bannerGraphic || COMPETITION_PLACEHOLDER_BANNER
          : undefined
      }
    >
      {portalOpen && Portal}
      {promoted && (
        <PromotedEyebrow>
          <FiAward size={15} />
          {promoted.title} Competition Clip
        </PromotedEyebrow>
      )}
      {item.title && <ClipTitle onClick={openClip}>{item.title}</ClipTitle>}
      {validTags.length > 0 && (
        <TagsRow>
          <ClipBadges tags={validTags} size="sm" maxVisible={4} />
        </TagsRow>
      )}
      <Header>
        <ClipAvatarLink onClick={goToProfile}>
          <Avatar
            size="sm"
            src={item.user.avatarUrl || undefined}
            userGroup={item.user.userGroup?.type as TUserGroup}
            rank={item.user.rank}
            profileCardUsername={item.username}
          />
        </ClipAvatarLink>
        <HeaderText>
          <HeaderLine>
            <ClipUsernameLink onClick={goToProfile}>
              {item.username}
            </ClipUsernameLink>
            {reasonLabel && ` ${reasonLabel}`}
          </HeaderLine>
          <HeaderMeta>{metaLine}</HeaderMeta>
        </HeaderText>
      </Header>
      <ThumbnailWrapper ref={registerRef} onClick={openClip}>
        <Thumbnail
          src={item.thumbnailStandardUrl}
          alt={item.title || "Clip thumbnail"}
        />
        <VideoOverlay $active={canAutoplay}>
          {canAutoplay && (
            <>
              <VideoPlayer
                refForVideoPlayer={videoPlayerRef}
                mp4Source={videoSrc}
                loop
                // Autoplay always has to start muted to satisfy browser policy,
                // but a fresh player should immediately pick up the shared
                // sound preference the moment playback actually begins rather
                // than resetting the viewer to muted on every new card.
                onPlay={() => {
                  if (!muted) videoPlayerRef.current?.muted(false);
                }}
                playAnalyticsProperties={{
                  shareId: item.shareId,
                  clipOwner: item.user,
                  total_views: item.views,
                }}
                watchAnalyticsProperties={{
                  item: item.shareId,
                  itemType: "Clip",
                  collection: "Activity Feed",
                  collectionType: "Generic",
                  action: "autoplay",
                }}
                options={{
                  controls: false,
                  aspectRatio: "16:9",
                  poster: item.thumbnailStandardUrl,
                  playsinline: true,
                  autoplay: "muted",
                  muted: true,
                  userActions: { click: false },
                }}
              />
              <MuteToggle
                onClick={toggleMute}
                aria-label={muted ? "Unmute" : "Mute"}
              >
                {muted ? <FaVolumeMute size={13} /> : <FaVolumeUp size={13} />}
              </MuteToggle>
            </>
          )}
        </VideoOverlay>
        <StatOverlay>
          {/* Gated on canAutoplay: TopComment runs its own per-clip comments
              query, so mounting it on every card would fan out to one request
              per feed item. */}
          {canAutoplay && item.id && (
            <TopComment clipId={item.id} onOpen={openComments} />
          )}
          <ClipStatBar
            views={item.views}
            likeCount={likeCount}
            commentCount={commentCount}
            isLiked={hasLiked}
            onView={openClip}
            onLike={submitLike}
            onComment={openComments}
          />
        </StatOverlay>
        {!canAutoplay && (
          <PlayOverlay className="feed-item-play">
            <PlayButton width={48} height={48} />
          </PlayOverlay>
        )}
        {/* Off-screen, not the real player — just gets the browser to start
            buffering this card's video before it's actually activated, so
            by the time it's scrolled to the network fetch is already warm
            instead of starting cold at the moment of activation. */}
        {preload && !canAutoplay && extras?.url && (
          <PreloadVideo src={extras.url} preload="auto" muted playsInline />
        )}
      </ThumbnailWrapper>
      {promoted && (
        <PromotedCta
          to={`/competitions/${promoted._id}`}
          onClick={(e: ReactMouseEvent) => e.stopPropagation()}
        >
          <PromotedCtaBody>
            <PromotedCtaTitle>
              {hasPrize ? (
                <>
                  Enter to win{" "}
                  <PromotedCtaPrize>{promotedPrize}</PromotedCtaPrize>
                </>
              ) : (
                "Submit your own clip"
              )}
            </PromotedCtaTitle>
            <PromotedCtaMeta>
              {promotedEndsIn && <span>{promotedEndsIn} to enter</span>}
              {!!promoted.participantCount && (
                <span>
                  {promoted.participantCount.toLocaleString()} already in
                </span>
              )}
            </PromotedCtaMeta>
          </PromotedCtaBody>
          <PromotedCtaAction>
            Enter
            <FiArrowRight size={14} />
          </PromotedCtaAction>
        </PromotedCta>
      )}
    </Card>
  );
};

const COMPETITION_BADGE_TEXT: Record<
  ReturnType<typeof getCompetitionPhase>,
  string
> = {
  active: "Live",
  upcoming: "Upcoming",
  ended: "Ended",
};

const CompetitionCountdown = ({
  item,
  phase,
}: {
  item: IActivityFeedCompetitionItem;
  phase: ReturnType<typeof getCompetitionPhase>;
}) => {
  const isUpcoming = phase === "upcoming";
  const time = useCountdownParts(isUpcoming ? item.startsAt : item.endsAt);

  return (
    <CompetitionCountdownBox>
      <CompetitionCountdownLabel>
        {isUpcoming ? "Starts In" : "Time Remaining"}
      </CompetitionCountdownLabel>
      <CompetitionCountdownDigits>
        <CompetitionCountdownCell>
          <CompetitionCountdownNum>{pad(time.d)}</CompetitionCountdownNum>
          <CompetitionCountdownUnit>Days</CompetitionCountdownUnit>
        </CompetitionCountdownCell>
        <CompetitionCountdownSep>:</CompetitionCountdownSep>
        <CompetitionCountdownCell>
          <CompetitionCountdownNum>{pad(time.h)}</CompetitionCountdownNum>
          <CompetitionCountdownUnit>Hrs</CompetitionCountdownUnit>
        </CompetitionCountdownCell>
        <CompetitionCountdownSep>:</CompetitionCountdownSep>
        <CompetitionCountdownCell>
          <CompetitionCountdownNum>{pad(time.m)}</CompetitionCountdownNum>
          <CompetitionCountdownUnit>Min</CompetitionCountdownUnit>
        </CompetitionCountdownCell>
        <CompetitionCountdownSep>:</CompetitionCountdownSep>
        <CompetitionCountdownCell>
          <CompetitionCountdownNum>{pad(time.s)}</CompetitionCountdownNum>
          <CompetitionCountdownUnit>Sec</CompetitionCountdownUnit>
        </CompetitionCountdownCell>
      </CompetitionCountdownDigits>
    </CompetitionCountdownBox>
  );
};

const CompetitionFeedCard = ({
  item,
}: {
  item: IActivityFeedCompetitionItem;
}) => {
  const theme = useTheme() as { colors: Record<string, string> };
  const history = useHistory();

  // Date-derived rather than trusting the feed's `reason` enum verbatim —
  // more robust than switching on exact reason strings the backend hasn't
  // fully confirmed (see IActivityFeedNode's reason comment), and it's the
  // same phase concept the competition detail page itself uses.
  const phase = getCompetitionPhase(item);
  const isCompleted = phase === "ended";
  const badgeVariant = phase === "active" ? "live" : phase;
  const isOfficial = item.creatorUser?.userGroup?.type === "INTERNAL";

  // Winners only matter once a competition is actually over — fetched
  // lazily rather than carried on every completed competition item in the
  // bulk feed query (score aggregation is the expensive part; prizes/entry
  // requirement/submission rule are cheap stored JSON and are fetched
  // eagerly above instead — see ACTIVITY_FEED_COMPETITION_FIELDS).
  const { data: extrasData } = useQuery(GET_ACTIVITY_FEED_COMPETITION_EXTRAS, {
    variables: { id: item._id },
    skip: !isCompleted,
  });
  const extras: IActivityFeedCompetitionExtras | undefined =
    extrasData?.competition;

  const winners = (extras?.leaderboard?.topEntries ?? [])
    .filter((e) => e.qualified !== false && e.rank != null && e.rank <= 3)
    .slice(0, 3);
  // Podium order — 2nd, 1st, 3rd — with 1st place rendered larger
  // (WinnerFeatureItem's $featured prop) so it reads as the featured winner
  // rather than just another name in a same-size row.
  const podium = [2, 1, 3]
    .map((rank) => winners.find((w) => w.rank === rank))
    .filter((entry): entry is (typeof winners)[number] => !!entry);

  const prizeLabel = getPrizeLabel(item.prizes);
  const ruleChips = getRuleChips(item);

  const goToProfile = (e: ReactMouseEvent, username: string) => {
    e.preventDefault();
    e.stopPropagation();
    trackFeedClick("profile", { username });
    history.push(`/u/${username}`);
  };

  const trackCardClick = () =>
    trackFeedClick("competition_card", { competitionId: item._id });

  return (
    <CompetitionCard to={`/competitions/${item._id}`} onClick={trackCardClick}>
      <CompetitionMedia>
        <CompetitionBanner
          $src={item.bannerGraphic || COMPETITION_PLACEHOLDER_BANNER}
        />
        <CompetitionScrim />
        {phase !== "ended" && (
          <CompetitionStatusBadge $variant={badgeVariant}>
            {phase === "active" && <LiveDot />}
            {COMPETITION_BADGE_TEXT[phase]}
          </CompetitionStatusBadge>
        )}
        <CompetitionOverlay>
          {isCompleted && (
            <CompetitionEndedRow>
              <FiFlag size={16} />
              Competition Ended
            </CompetitionEndedRow>
          )}
          <CompetitionTitle>{item.competitionTitle}</CompetitionTitle>

          <CompetitionByline>
            {item.creatorUser && (
              <CompetitionCreatorLink
                onClick={(e: ReactMouseEvent) =>
                  goToProfile(e, item.creatorUser!.username)
                }
              >
                <Avatar
                  src={item.creatorUser.avatarUrl || undefined}
                  userGroup={item.creatorUser.userGroup?.type as TUserGroup}
                  size="xs"
                  profileCardUsername={item.creatorUser.username}
                />
                <CompetitionCreatorLabel>by</CompetitionCreatorLabel>
                <CompetitionCreatorName>
                  {item.creatorUser.username}
                </CompetitionCreatorName>
              </CompetitionCreatorLink>
            )}
            {isOfficial && (
              <>
                {item.creatorUser && <CompetitionMetaDivider />}
                <CompetitionOfficialTag>
                  <FiCheckCircle size={13} />
                  Official
                </CompetitionOfficialTag>
              </>
            )}
            {typeof item.participantCount === "number" && (
              <>
                {(item.creatorUser || isOfficial) && <CompetitionMetaDivider />}
                <CompetitionMetaCount>
                  <FiUsers size={13} />
                  <b>{item.participantCount.toLocaleString()}</b>
                  Participants
                </CompetitionMetaCount>
              </>
            )}
          </CompetitionByline>

          {prizeLabel && prizeLabel !== "Just for fun" && (
            <CompetitionPrizeBlock>
              <CompetitionPrizeLabel>Grand Prize</CompetitionPrizeLabel>
              <CompetitionPrizeValue>{prizeLabel}</CompetitionPrizeValue>
            </CompetitionPrizeBlock>
          )}

          {!isCompleted && <CompetitionCountdown item={item} phase={phase} />}

          {ruleChips.length > 0 && (
            <CompetitionChipsRow>
              {ruleChips.map((chip) => (
                <CompetitionChip key={chip.label}>
                  <chip.icon size={11} />
                  {chip.label}
                </CompetitionChip>
              ))}
            </CompetitionChipsRow>
          )}

          {isCompleted && podium.length > 0 && (
            <WinnersSection>
              <WinnersHeading>
                <FiAward size={13} />
                Winners
              </WinnersHeading>
              <WinnersFeatureRow>
                {podium.map((entry) => {
                  const accent = rankAccent(entry.rank, theme);
                  const featured = entry.rank === 1;
                  return (
                    <WinnerFeatureItem
                      key={entry.userId}
                      $featured={featured}
                      onClick={(e: ReactMouseEvent) =>
                        goToProfile(e, entry.user?.username ?? entry.userId)
                      }
                    >
                      <WinnerAvatarRing
                        $color={accent?.color}
                        $featured={featured}
                      >
                        <WinnerFeatureAvatar
                          src={entry.user?.avatarUrl || undefined}
                          alt=""
                        />
                        <WinnerRankBadge
                          $color={accent?.color}
                          $featured={featured}
                        >
                          {entry.rank}
                        </WinnerRankBadge>
                      </WinnerAvatarRing>
                      <WinnerFeatureName $featured={featured}>
                        {entry.user?.username ?? "Unknown"}
                      </WinnerFeatureName>
                      <WinnerFeatureScore
                        $color={accent?.color}
                        $featured={featured}
                      >
                        {Math.round(entry.score).toLocaleString()}
                      </WinnerFeatureScore>
                    </WinnerFeatureItem>
                  );
                })}
              </WinnersFeatureRow>
            </WinnersSection>
          )}
        </CompetitionOverlay>
      </CompetitionMedia>
    </CompetitionCard>
  );
};

const BlogPostFeedCard = ({ item }: { item: IActivityFeedBlogPostItem }) => {
  const displayName = item.nickname || item.author;

  const trackCardClick = () => trackFeedClick("blog_card", { slug: item.slug });

  return (
    <BlogCard to={`/blog/${item.slug}`} onClick={trackCardClick}>
      {item.avatarUrl && <BlogBackgroundAvatar src={item.avatarUrl} alt="" />}
      <BlogContent>
        <BlogTag>
          <FiFileText size={26} />
          Blog
        </BlogTag>
        <BlogTitle>{item.postTitle}</BlogTitle>
        <BlogByline>
          {(item.avatarUrl || displayName) && (
            <BlogAuthorGroup>
              {item.avatarUrl && <BlogAvatar src={item.avatarUrl} alt="" />}
              {displayName && <BlogAuthorName>{displayName}</BlogAuthorName>}
            </BlogAuthorGroup>
          )}
          {item.publishedDate && (
            <span>{dateFormatWithDistanceSuffix(item.publishedDate)}</span>
          )}
        </BlogByline>
      </BlogContent>
    </BlogCard>
  );
};

export const FeedItem = ({
  node,
  isActive,
  preload,
  registerRef,
}: {
  node: IActivityFeedNode;
  isActive: boolean;
  preload: boolean;
  registerRef: (node: HTMLElement | null) => void;
}) => {
  const { item, reason } = node;

  if (item.__typename === "Clip") {
    return (
      <ClipFeedCard
        item={item}
        reason={reason}
        isActive={isActive}
        preload={preload}
        registerRef={registerRef}
      />
    );
  }

  if (item.__typename === "Competition") {
    return <CompetitionFeedCard item={item} />;
  }

  if (item.__typename === "BlogPost") {
    return <BlogPostFeedCard item={item} />;
  }

  if (item.__typename === "PlayedMatch") {
    return <PlayedMatchFeedCard item={item} />;
  }

  // Unrecognized/future item type — degrade gracefully rather than break.
  return null;
};
